"use client"

import { BlockchainsMoleculeReatom } from "@/app/blockchains/blockchain-molecule.reatom"
import { BlockchainFiltersMoleculeReatom } from "@/app/blockchains/filters/blockchain-filters-molecule.reatom"
import type { BlockchainFilter } from "@/app/blockchains/filters/blockchain-filters-scope"
import { getRoutableBlockchains } from "@/app/blockchains/supported-blockchains"
import type { Blockchain } from "@anaconda/core/blockchain/types"
import { reatomComponent, useWrap } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { notFound, usePathname } from "next/navigation"
import { useEffect } from "react"

const ROUTABLE_BLOCKCHAINS = new Set<Blockchain>(getRoutableBlockchains())

interface Props {
  children: React.ReactNode
  blockchain: BlockchainFilter
}

export const BlockchainSyncWrapper = reatomComponent<Props>(
  ({ children, blockchain }) => {
    const { setSelectedBlockchain, selectedBlockchain } = useMolecule(
      BlockchainFiltersMoleculeReatom,
    )
    const { supportedBlockchains } = useMolecule(BlockchainsMoleculeReatom)

    // Read URL to detect client-side navigation
    const pathname = usePathname()

    // Extract blockchain from URL (first path segment)
    const pathSegments = pathname.split("/").filter(Boolean)
    const urlBlockchain = (pathSegments[0]?.toUpperCase() ||
      blockchain) as BlockchainFilter

    // Reatom v1000: Call atoms directly as functions (NO useAtom hook!)
    const supportedBlockchainsValue = supportedBlockchains()
    const currentSelected = selectedBlockchain()

    const isUrlBlockchainSupported =
      urlBlockchain === "ALL" ||
      ROUTABLE_BLOCKCHAINS.has(urlBlockchain as Blockchain) ||
      supportedBlockchainsValue.includes(urlBlockchain)

    // Only validate blockchain for actual blockchain routes
    // Skip validation for brand routes (blockchain prop === "ALL")
    if (
      blockchain !== "ALL" &&
      urlBlockchain !== "ALL" &&
      !isUrlBlockchainSupported
    ) {
      notFound()
    }

    // Sync URL blockchain with Reatom state in useEffect to catch URL changes from pushState
    // For blockchain routes: use urlBlockchain from the actual URL (not the stale blockchain prop)
    // For brand routes: urlBlockchain will be the brand slug (e.g., "VEEFRIENDS"), so treat it as "ALL"
    const syncBlockchain = useWrap(() => {
      const blockchainToSet = isUrlBlockchainSupported ? urlBlockchain : "ALL"

      if (blockchainToSet !== currentSelected) {
        setSelectedBlockchain(blockchainToSet)
      }
    })

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      syncBlockchain()
    }, [blockchain, urlBlockchain, currentSelected, syncBlockchain])

    return <>{children}</>
  },
  "BlockchainSyncWrapper",
)
